import React, { useReducer, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarDaysIcon, PlusIcon, InformationCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import moment from 'moment';

// --- START: MOCK DATA & STATE MANAGEMENT LOGIC ---

// Initial state for the reducer
const initialState = {
    balance: { casual: 12, sick: 8, earned: 15 },
    history: [],
    application: { type: 'casual', startDate: '', endDate: '', reason: '' },
    isModalOpen: false,
    isLoading: true,
    error: null,
};

// Reducer function to handle state transitions
const leaveReducer = (state, action) => {
    switch (action.type) {
        case 'FETCH_SUCCESS':
            return {
                ...state,
                balance: action.payload.balance,
                history: action.payload.history,
                isLoading: false,
            };
        case 'FETCH_ERROR':
            return {
                ...state,
                isLoading: false,
                error: 'Failed to fetch leave data.',
            };
        case 'UPDATE_APPLICATION_FIELD':
            return {
                ...state,
                application: {
                    ...state.application,
                    [action.field]: action.value,
                },
            };
        case 'SUBMIT_APPLICATION':
            const newHistoryItem = {
                ...action.payload,
                status: 'Pending',
                submittedAt: new Date().toISOString(),
                id: Date.now(),
            };
            return {
                ...state,
                history: [newHistoryItem, ...state.history],
                isModalOpen: false,
                application: initialState.application, // Reset form
            };
        case 'TOGGLE_MODAL':
            return {
                ...state,
                isModalOpen: !state.isModalOpen,
            };
        default:
            return state;
    }
};

// Custom Hook to manage leave data and API simulation
const useLeaveData = () => {
    const [state, dispatch] = useReducer(leaveReducer, initialState);

    useEffect(() => {
        // Simulate an API call
        const fetchLeaveData = async () => {
            try {
                await new Promise((resolve) => setTimeout(resolve, 1500)); // Simulate network delay
                const mockData = {
                    balance: { casual: 10, sick: 8, earned: 15 },
                    history: [
                        { id: 1, type: 'sick', startDate: '2025-08-10', endDate: '2025-08-12', reason: 'Fever', status: 'Approved', submittedAt: '2025-08-01' },
                        { id: 2, type: 'casual', startDate: '2025-09-05', endDate: '2025-09-06', reason: 'Personal work', status: 'Pending', submittedAt: '2025-09-01' },
                    ],
                };
                dispatch({ type: 'FETCH_SUCCESS', payload: mockData });
            } catch (error) {
                dispatch({ type: 'FETCH_ERROR', error: error.message });
            }
        };

        fetchLeaveData();
    }, []);

    const handleSubmit = () => {
        dispatch({ type: 'SUBMIT_APPLICATION', payload: state.application });
    };

    return { state, dispatch, handleSubmit };
};

// --- END: MOCK DATA & STATE MANAGEMENT LOGIC ---

const LeaveBalance = () => {
    const { state, dispatch, handleSubmit } = useLeaveData();
    const { balance, history, application, isModalOpen, isLoading, error } = state;
    const [selectedLeaveType, setSelectedLeaveType] = useState(null);

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
        exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2 } },
    };

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="flex flex-col items-center">
                    <ArrowPathIcon className="h-12 w-12 text-blue-500 animate-spin" />
                    <p className="mt-4 text-xl font-medium text-gray-700">Loading data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center p-8 bg-white rounded-lg shadow-xl">
                    <h2 className="text-2xl font-bold text-red-600">Error</h2>
                    <p className="mt-2 text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 p-8 text-gray-800">
            <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="max-w-7xl mx-auto"
            >
                {/* Header */}
                <header className="flex justify-between items-center mb-6">
                    <div className="flex items-center">
                        <CalendarDaysIcon className="h-10 w-10 text-indigo-600 mr-4" />
                        <h1 className="text-4xl font-extrabold text-indigo-800">Leave Management</h1>
                    </div>
                    <button
                        onClick={() => dispatch({ type: 'TOGGLE_MODAL' })}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-full shadow-lg transform transition-transform duration-200 hover:scale-105 flex items-center"
                    >
                        <PlusIcon className="h-5 w-5 mr-2" />
                        Apply for Leave
                    </button>
                </header>

                {/* Leave Balance Overview */}
                <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {Object.entries(balance).map(([type, count]) => {
                        const usedLeaves = history.filter(h => h.type === type && h.status === 'Approved').length;
                        return (
                            <motion.div
                                key={type}
                                onClick={() => setSelectedLeaveType({ type, count, usedLeaves })}
                                className="bg-white p-6 rounded-3xl shadow-lg flex flex-col items-center cursor-pointer"
                                whileHover={{ scale: 1.03 }}
                                transition={{ type: 'spring', stiffness: 300 }}
                            >
                                <InformationCircleIcon className="h-12 w-12 text-indigo-400" />
                                <h3 className="text-xl font-bold mt-2 text-gray-900 capitalize">{type} Leave</h3>
                                <p className="text-5xl font-extrabold text-indigo-600 mt-2">{count}</p>
                                <p className="text-xs text-gray-500 mt-1">Click for details</p>
                            </motion.div>
                        );
                    })}
                </section>

                {/* Leave History Table */}
                <section className="bg-white rounded-3xl shadow-lg overflow-hidden">
                    <div className="p-6">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Leave History</h2>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {history.map((item) => (
                                        <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 capitalize">{item.type}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {moment(item.startDate).format('MMM D, YYYY')} - {moment(item.endDate).format('MMM D, YYYY')}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.reason}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${item.status === 'Approved' ? 'bg-green-100 text-green-800' : item.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                                                    {item.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            </motion.div>

            {/* Leave Details Popup */}
            <AnimatePresence>
                {selectedLeaveType && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50"
                        onClick={() => setSelectedLeaveType(null)}
                    >
                        <motion.div
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            className="bg-white rounded-3xl shadow-xl w-full max-w-md p-8 relative"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={() => setSelectedLeaveType(null)}
                                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                            <h3 className="text-2xl font-bold mb-6 text-gray-900 capitalize">{selectedLeaveType.type} Leave Details</h3>
                            <div className="space-y-4">
                                <div className="bg-indigo-50 p-4 rounded-xl">
                                    <p className="text-sm text-gray-600">Total Leaves Used</p>
                                    <p className="text-3xl font-bold text-indigo-600">{selectedLeaveType.usedLeaves}</p>
                                </div>
                                <div className="bg-green-50 p-4 rounded-xl">
                                    <p className="text-sm text-gray-600">Pending Leave Balance</p>
                                    <p className="text-3xl font-bold text-green-600">{selectedLeaveType.count}</p>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-xl">
                                    <p className="text-sm text-gray-600">Total Allocated</p>
                                    <p className="text-3xl font-bold text-gray-700">{selectedLeaveType.count + selectedLeaveType.usedLeaves}</p>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Leave Application Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50"
                    >
                        <motion.div
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            className="bg-white rounded-3xl shadow-xl w-full max-w-md p-8 relative"
                        >
                            <button
                                onClick={() => dispatch({ type: 'TOGGLE_MODAL' })}
                                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                            <h3 className="text-2xl font-bold mb-6 text-gray-900">Apply for Leave</h3>
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSubmit();
                                }}
                                className="space-y-6"
                            >
                                <div>
                                    <label htmlFor="type" className="block text-sm font-medium text-gray-700">Leave Type</label>
                                    <select
                                        id="type"
                                        name="type"
                                        value={application.type}
                                        onChange={(e) => dispatch({ type: 'UPDATE_APPLICATION_FIELD', field: 'type', value: e.target.value })}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
                                    >
                                        <option value="casual">Casual Leave</option>
                                        <option value="sick">Sick Leave</option>
                                        <option value="earned">Earned Leave</option>
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">Start Date</label>
                                    <input
                                        type="date"
                                        id="startDate"
                                        name="startDate"
                                        value={application.startDate}
                                        onChange={(e) => dispatch({ type: 'UPDATE_APPLICATION_FIELD', field: 'startDate', value: e.target.value })}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">End Date</label>
                                    <input
                                        type="date"
                                        id="endDate"
                                        name="endDate"
                                        value={application.endDate}
                                        onChange={(e) => dispatch({ type: 'UPDATE_APPLICATION_FIELD', field: 'endDate', value: e.target.value })}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="reason" className="block text-sm font-medium text-gray-700">Reason</label>
                                    <textarea
                                        id="reason"
                                        name="reason"
                                        rows="3"
                                        value={application.reason}
                                        onChange={(e) => dispatch({ type: 'UPDATE_APPLICATION_FIELD', field: 'reason', value: e.target.value })}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
                                        required
                                    ></textarea>
                                </div>
                                <div className="flex justify-end">
                                    <button
                                        type="submit"
                                        className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                    >
                                        Submit Application
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default LeaveBalance;